import { Component, computed, effect, EffectRef, ElementRef, inject, Signal, ViewChild, NgZone  } from '@angular/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { ButtonComponent } from '@components/shared/button/button.component';
import { ButtonType } from '@models/enums/components/Button/ButtonType.enum';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { View3dDisplay } from '@models/types/components/View3dDisplay.type';
import { AmenitiesService } from '@services/amenities.service';
import { RelatedPreviewsService } from '@services/components/related-previews.service';
import { DvmService } from '@services/dvm.service';
import { PopoverService } from '@services/popover.service';
import { TranslationService } from '@services/translation.service';
import { UiService } from '@services/ui.service';
import anime from 'animejs';

@Component({
  selector: 'app-hyperlink',
  standalone: true,
  imports: [
    ButtonComponent
  ],
  templateUrl: './hyperlink.component.html',
  styleUrl: './hyperlink.component.css'
})
export class HyperlinkComponent {

  @ViewChild('hyperlinkBtn', { read: ElementRef })
  private readonly hyperlinkBtn!: ElementRef<HTMLElement>;

  // SERVICES
  private readonly dvmService:      DvmService             = inject(DvmService);
  private readonly router:          Router                 = inject(Router); 
  private readonly popover:         PopoverService         = inject(PopoverService);
  private readonly route:           ActivatedRoute         = inject(ActivatedRoute);
  private readonly ngZone:          NgZone                 = inject(NgZone);
  private readonly ams:             AmenitiesService       = inject(AmenitiesService);
  private readonly relatedPreviews: RelatedPreviewsService = inject(RelatedPreviewsService);
  public  readonly translation:     TranslationService     = inject(TranslationService);
  
  

  // CONFIG
    private readonly uiconfig:              Signal<UiConfiguration>  = inject(UiService).uiConfig;
    private readonly view3dDisplay:         Signal<View3dDisplay>    = computed((): View3dDisplay=> this.uiconfig().View3dDisplay);
  

  // GLOBAL STATE
  public readonly  view:          Signal<AppViews| null> = this.dvmService.view;
  protected readonly hasViewId:   boolean                = this.route.snapshot.queryParamMap.has('view_id');


  // BUTTON PROPERTIES
  public readonly  type:          ButtonType             = ButtonType.Default;
  public           active:        boolean                = false;

  // COMPONENT STATE
  public           showElement:   boolean                = this.view() === AppViews.Pano;
  public           showLabel:     boolean                = false;
  private readonly timer:         number                 = 1500
  private readonly animation:     number                 = 300;

  // EFFECTS
  public readonly  onViewChange:  EffectRef              = effect((): void => {

    const view: AppViews | null = this.view(),
          show: boolean = view === AppViews.Pano && this.view3dDisplay().enabled && this.view3dDisplay().copySeatLink && !this.hasViewId;

    if(view === null) return;

    if (!this.showElement) {
      setTimeout((): void => this.animateHyperlink(show), 300);
    } 
    else {
      this.animateHyperlink(show);
    }
    
  });


  // METHODS
  /**
   * Genera un enlace a la vista 3D actual y lo copia al portapapeles.
   * Release notes (0.11.1): Pasamos este método a ser async para mejorar el refactor y evitar que si la copia falla, 
   * la animacion se quede en un estado activo. 
   */
  public async copyLink(): Promise<void> {

    this.togleActiveState(true);

    try {
      await navigator.clipboard.writeText(this.generateUrl());
    } 
    catch (error) {
      console.error('Failed to copy: ', error);
    }
    finally {
      setTimeout((): void => this.togleActiveState(false), (this.timer + this.animation));
    }

    
  }

  public forceClose(): void{
    this.togleActiveState(false);
  }

  /**
   * Genera la URL para la vista 3D actual.
   * La URL se genera a partir del ID de la vista 3D actual y los parámetros de consulta proporcionaods.
   * @returns La URL generada.
   */
  private generateUrl(): string {

    const view3dId: string  = btoa(this.dvmService.viewer3d.getViewId()!),
          amenities: string = btoa(this.ams.selectedAmenities().map(a => a.id.toString()).join(',')),
          relatedPreview: string = btoa(this.relatedPreviews.currentPreview()?.label || '') ,
          urlTree:  UrlTree = this.router.createUrlTree(
            [], 
            {
              queryParams: {
                view_id: view3dId,
                amenities: amenities.length ? amenities : null,
                related_preview: relatedPreview ? relatedPreview : null
              }
            }
          ),
          finalUrl: string  = location.origin + this.router.serializeUrl(urlTree);

    return finalUrl;
     
  }

  /**
   * Método que anima el botón de enlace para mostrar u ocultar el elemento.
   * @param show 
   */
  private animateHyperlink(show: boolean): void {
    
    anime({
      begin:    (): void => { if(show && !this.showElement) this.showElement = true },
      complete: (): void => { if(!show && this.showElement) this.showElement = false },
      targets:  this.hyperlinkBtn.nativeElement,
      opacity:  show ? 1 : 0,
      duration: 300,
      easing:   'easeInOutQuad',
    })

  }

  /**
   * Método que alterna el estado activo del botón de enlace y anima su transición.
   * La animación depende del estado que se le pase, si es activo se ensanchará y si no retornará a su tamaño original.
   * El funcionamiento es el siguiente:
   * 1. Nada más comenzar, si el estado es true, se activa inmediatamente el estado activo del botón.
   * 2. Durante la animación, se comprueba si el estado es true o false para mostrar u ocultar el texto del botón.
   *  - Si es true, se espera a que la animación alcance un 80% de su progreso para mostrar el texto.
   *  - Si es false, se espera a que la animación alcance un 20% de su progreso para ocultar el texto.
   * 3. Al finalizar la animación, si es false, se desactiva el estado activo del botón. (Una vez vuelta a su tamaño original).
   * @param state Estado activo del botón.
   */

  private togleActiveState(state: boolean): void {
    const targets: HTMLButtonElement = this.hyperlinkBtn.nativeElement.children[0] as HTMLButtonElement;

    anime({
      begin: (): void => {
        if (state) {
          //let angular know to check zone //TODO: potentially make a zoneAware helper to be used in the components 
          this.ngZone.run(() => {
            this.active = true;
          });
        }
      },
      update: (animation): void => {
        const threshold: number = state ? 80 : 20;
        if (animation.progress >= threshold) {
          this.ngZone.run(() => {
            this.showLabel = state;
          });
        }
      },
      complete: (): void => {
        if (!state) {
          this.ngZone.run(() => {
            this.active = false;
          });
        }
      },
      targets,
      width: state ? 145 : 34,
      easing: 'easeInOutQuad',
      duration: this.animation,
    });
  }

}
